/**
 * Inline SVG charts (no chart library). Returns markup strings.
 */
import { MONTHS_SHORT, usdRound } from "./viewmodel";

export interface BarsOptions {
  months: number[];
  today: number[];
  octopus: number[];
  /** Calendar month the term starts in, used for year separators. */
  title: string;
  compact?: boolean;
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** Picks a "nice" axis maximum and step. */
function niceScale(max: number): { top: number; step: number } {
  if (max <= 0) return { top: 100, step: 25 };
  const rough = max / 4;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough) ?? rough;
  return { top: Math.ceil(max / step) * step, step };
}

/** Grouped monthly bars: Today vs Octopus. */
export function monthlyBars(o: BarsOptions): string {
  const n = o.months.length;
  const W = 960;
  const H = o.compact ? 230 : 320;
  const padL = 64;
  const padR = 12;
  const padT = 16;
  const padB = n > 12 ? 52 : 40;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const { top, step } = niceScale(Math.max(...o.today, ...o.octopus, 0));
  const y = (v: number): number => padT + plotH - (Math.max(0, v) / top) * plotH;
  const slot = plotW / n;
  const barW = Math.max(3, slot * 0.36);

  const parts: string[] = [];
  for (let v = 0; v <= top + 1e-9; v += step) {
    const yy = y(v).toFixed(1);
    parts.push(`<line class="grid" x1="${padL}" x2="${W - padR}" y1="${yy}" y2="${yy}"/>`);
    parts.push(`<text class="axis" x="${padL - 8}" y="${yy}" text-anchor="end" dominant-baseline="middle">${usdRound(v)}</text>`);
  }
  o.months.forEach((m, i) => {
    const x0 = padL + i * slot + (slot - barW * 2 - 2) / 2;
    const tA = o.today[i] ?? 0;
    const tC = o.octopus[i] ?? 0;
    parts.push(
      `<rect class="bar-today" x="${x0.toFixed(1)}" y="${y(tA).toFixed(1)}" width="${barW.toFixed(1)}" height="${(padT + plotH - y(tA)).toFixed(1)}" rx="3"><title>${MONTHS_SHORT[m]} today: $${tA.toFixed(2)}</title></rect>`,
      `<rect class="bar-octopus" x="${(x0 + barW + 2).toFixed(1)}" y="${y(tC).toFixed(1)}" width="${barW.toFixed(1)}" height="${(padT + plotH - y(tC)).toFixed(1)}" rx="3"><title>${MONTHS_SHORT[m]} Octopus: $${tC.toFixed(2)}</title></rect>`,
    );
    const label = n > 12 ? MONTHS_SHORT[m]!.slice(0, 1) : MONTHS_SHORT[m]!;
    const cx = (padL + i * slot + slot / 2).toFixed(1);
    parts.push(`<text class="axis" x="${cx}" y="${H - padB + 18}" text-anchor="middle">${label}</text>`);
    if (n > 12 && m === 0 && i > 0) {
      const xs = (padL + i * slot).toFixed(1);
      parts.push(`<line class="year-sep" x1="${xs}" x2="${xs}" y1="${padT}" y2="${H - padB + 4}"/>`);
    }
    if (n > 12 && (i === 0 || m === 0)) {
      parts.push(`<text class="axis year" x="${(padL + i * slot + 2).toFixed(1)}" y="${H - 8}">${i === 0 ? "Start" : "New year"}</text>`);
    }
  });

  return `<svg class="chart-bars" viewBox="0 0 ${W} ${H}" role="img" aria-labelledby="chart-t" preserveAspectRatio="xMidYMid meet">
<title id="chart-t">${esc(o.title)}</title>
${parts.join("")}
</svg>`;
}
